/**
 * Employee Timesheet Statement Export
 *
 * Opens a print-ready invoice-style timesheet for a single employee across
 * the given period. Pulls the company profile (name, address, logo, phone,
 * email, website) from `localStorage.maidly_settings.companyInfo` so it
 * matches the branding used everywhere else in the app (invoices, quotes).
 *
 * The window shows a Print/Save-as-PDF button that triggers the native
 * browser print dialog — no new dependency needed.
 */
import { liveWorkedMinutes, toISODate, formatHM } from '../../utils/timeClockHelpers';

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDate = (d) =>
  d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const fmtDay = (d) =>
  d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const fmtHours = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};

const fmtMoney = (n) => `$${(n || 0).toFixed(2)}`;

const loadCompanyInfo = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    return settings.companyInfo || {};
  } catch { return {}; }
};

/**
 * @param {object} emp    { id, name, email, role, hourlyRate, phone, address }
 * @param {array}  entries  Full list; we filter by empId + period.
 * @param {Date}   periodStart
 * @param {Date}   periodEnd   inclusive
 */
export const exportEmployeeStatement = (emp, entries, periodStart, periodEnd) => {
  const company = loadCompanyInfo();
  const companyName = company.name || 'Maidly Business Manager';
  const companyLogo = company.logo || '';
  const companyAddress = company.address || '';
  const companyPhone = company.phone || '';
  const companyEmail = company.email || '';
  const companyWebsite = company.website || '';

  const startIso = toISODate(periodStart);
  const endIso = toISODate(periodEnd);

  const empEntries = entries
    .filter((e) => e.employeeId === emp.id && e.startTime)
    .filter((e) => {
      const ds = toISODate(new Date(e.startTime));
      return ds >= startIso && ds <= endIso;
    })
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  let regularMin = 0;
  let ptoMin = 0;
  let sickMin = 0;
  let totalPay = 0;

  const rows = empEntries.map((e, idx) => {
    const mins = liveWorkedMinutes(e);
    const kind = e.type || 'shift';
    if (kind === 'pto') ptoMin += mins;
    else if (kind === 'sick') sickMin += mins;
    else regularMin += mins;
    const hours = mins / 60;
    const rate = parseFloat(e.hourlyRate ?? emp.hourlyRate) || 0;
    const pay = kind === 'unpaid' ? 0 : hours * rate;
    totalPay += pay;
    return `
      <tr>
        <td class="idx">${idx + 1}</td>
        <td>${esc(fmtDay(new Date(e.startTime)))}</td>
        <td><span class="pill pill-${kind}">${esc(kind.toUpperCase())}</span></td>
        <td>${esc(e.job || '—')}</td>
        <td>${esc(formatHM(e.startTime))}</td>
        <td>${esc(e.endTime ? formatHM(e.endTime) : '—')}</td>
        <td class="num">${esc(fmtHours(mins))}</td>
        <td class="num">${esc(fmtMoney(rate))}</td>
        <td class="num pay">${esc(fmtMoney(pay))}</td>
      </tr>
    `;
  }).join('');

  const totalMin = regularMin + ptoMin + sickMin;
  const generatedAt = new Date().toLocaleString();

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Timesheet Statement — ${esc(emp.name)} — ${esc(startIso)} to ${esc(endIso)}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif;
      margin: 0; padding: 36px; color: #0f172a; background: #eef2f7;
    }
    .page {
      max-width: 880px; margin: 0 auto;
      background: #ffffff; border-radius: 14px;
      box-shadow: 0 20px 45px rgba(15,23,42,0.12);
      overflow: hidden;
    }
    .actions {
      display: flex; gap: 10px; justify-content: flex-end;
      padding: 14px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
    }
    .btn {
      padding: 9px 16px; border-radius: 9px; border: 1px solid #e2e8f0;
      background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
      color: #0f172a;
    }
    .btn:hover { background: #f1f5f9; }
    .btn-primary {
      background: #0073ea; color: #fff; border-color: #0073ea;
      box-shadow: 0 3px 10px rgba(0,115,234,0.28);
    }
    .btn-primary:hover { background: #0060c4; }

    /* Header band */
    header {
      background: linear-gradient(135deg, #0073ea 0%, #005ec2 100%);
      color: #fff; padding: 32px 40px 28px;
      display: grid; grid-template-columns: auto 1fr auto; gap: 24px; align-items: center;
    }
    header .logo {
      width: 84px; height: 84px; border-radius: 14px;
      background: #ffffff; display: flex; align-items: center; justify-content: center;
      overflow: hidden; box-shadow: 0 6px 18px rgba(0,0,0,0.15);
    }
    header .logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
    header .company-address {
      font-size: 13px; color: rgba(255,255,255,0.92); font-weight: 500;
      line-height: 1.55; letter-spacing: 0.2px; white-space: pre-line;
      align-self: center;
    }
    header .doc { text-align: right; }
    header .doc .kind { font-size: 12px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.85; }
    header .doc h1 { margin: 4px 0 0; font-size: 34px; letter-spacing: -0.02em; }

    /* Meta info bar */
    .meta {
      padding: 22px 40px;
      display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 32px;
      border-bottom: 1px solid #e2e8f0;
    }
    .meta h4 {
      margin: 0 0 6px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 1.6px; color: #64748b; font-weight: 700;
    }
    .meta .val { font-size: 14.5px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
    .meta .sub { font-size: 12px; color: #64748b; margin-top: 2px; line-height: 1.4; white-space: pre-line; }

    /* Summary cards */
    .cards {
      padding: 22px 40px 6px;
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
    }
    .card {
      border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px;
      background: #f8fafc;
    }
    .card h4 {
      margin: 0 0 4px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 1.6px; color: #64748b; font-weight: 700;
    }
    .card .n { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; color: #0f172a; }
    .card.accent { background: linear-gradient(135deg, #0073ea 0%, #005ec2 100%); color: #fff; border-color: #0073ea; }
    .card.accent h4 { color: rgba(255,255,255,0.9); }
    .card.accent .n { color: #fff; }

    /* Table */
    .table-wrap { padding: 20px 40px 32px; }
    .table-title {
      font-size: 12px; text-transform: uppercase; letter-spacing: 1.6px;
      color: #64748b; font-weight: 700; margin: 8px 0 10px;
    }
    table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
    thead th {
      background: #0f172a; color: #fff;
      text-align: left; padding: 10px 10px;
      font-size: 10.5px; text-transform: uppercase; letter-spacing: 1.2px; font-weight: 700;
    }
    thead th:first-child { border-top-left-radius: 8px; }
    thead th:last-child  { border-top-right-radius: 8px; text-align: right; }
    tbody td {
      padding: 10px 10px; border-bottom: 1px solid #e2e8f0;
      vertical-align: middle;
    }
    tbody tr:nth-child(even) td { background: #f8fafc; }
    .idx { color: #94a3b8; font-weight: 600; width: 32px; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    td.pay { font-weight: 700; color: #0f172a; }

    .pill {
      display: inline-block; padding: 3px 9px; border-radius: 999px;
      font-size: 10px; font-weight: 700; letter-spacing: 0.6px;
    }
    .pill-shift  { background: #dcfce7; color: #14532d; }
    .pill-pto    { background: #ede9fe; color: #5b21b6; }
    .pill-sick   { background: #fef3c7; color: #92400e; }
    .pill-unpaid { background: #f1f5f9; color: #334155; }

    tfoot td {
      padding: 14px 10px 6px !important;
      border-top: 2px solid #0f172a;
      font-weight: 700;
    }
    tfoot td.label { text-align: right; color: #64748b; text-transform: uppercase; letter-spacing: 1px; font-size: 11px; }
    tfoot td.grand { font-size: 15px; color: #0073ea; }

    /* Signatures + footer */
    .sig {
      display: grid; grid-template-columns: 1fr 1fr; gap: 60px;
      padding: 26px 40px 22px; border-top: 1px solid #e2e8f0;
    }
    .sig .line {
      border-top: 1px solid #0f172a; padding-top: 8px;
      font-size: 11px; color: #64748b; letter-spacing: 0.5px;
      text-transform: uppercase; font-weight: 600;
    }

    footer {
      display: flex; justify-content: space-between; align-items: center;
      padding: 16px 40px; background: #0f172a; color: #cbd5e1;
      font-size: 11px; letter-spacing: 0.3px;
    }
    footer .code { font-family: 'SF Mono', ui-monospace, monospace; opacity: 0.9; }

    .empty {
      padding: 40px; text-align: center; color: #64748b; font-style: italic;
      background: #f8fafc; border-radius: 10px;
    }

    @media print {
      body { padding: 0; background: #ffffff; }
      .page { box-shadow: none; border-radius: 0; }
      .actions { display: none; }
      header {
        -webkit-print-color-adjust: exact; print-color-adjust: exact;
      }
      .card.accent, thead th, footer, tfoot td.grand {
        -webkit-print-color-adjust: exact; print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="actions">
      <button class="btn" onclick="window.close()">Close</button>
      <button class="btn btn-primary" onclick="window.print()">🖨️ Print / Save as PDF</button>
    </div>

    <header>
      ${companyLogo ? `
        <div class="logo">
          <img src="${esc(companyLogo)}" alt="${esc(companyName)} logo" />
        </div>
      ` : `<div></div>`}
      <div class="company-address">${
        esc([companyAddress, companyPhone, companyEmail, companyWebsite].filter(Boolean).join('\n'))
      }</div>
      <div class="doc">
        <div class="kind">Timesheet Statement</div>
        <h1>${fmtHours(totalMin)}</h1>
      </div>
    </header>

    <div class="meta">
      <div>
        <h4>Employee</h4>
        <div class="val">${esc(emp.name)}</div>
        <div class="sub">${esc([emp.email, emp.role && emp.role[0].toUpperCase() + emp.role.slice(1), emp.phone].filter(Boolean).join('\n'))}</div>
      </div>
      <div>
        <h4>Pay period</h4>
        <div class="val">${esc(fmtDate(periodStart))}</div>
        <div class="sub">to ${esc(fmtDate(periodEnd))}
        · ${empEntries.length} shift${empEntries.length === 1 ? '' : 's'}</div>
      </div>
      <div>
        <h4>Pay rate</h4>
        <div class="val">${esc(fmtMoney(parseFloat(emp.hourlyRate) || 0))} / hour</div>
        <div class="sub">Issued ${esc(generatedAt)}</div>
      </div>
    </div>

    <div class="cards">
      <div class="card">
        <h4>Regular</h4>
        <div class="n">${esc(fmtHours(regularMin))}</div>
      </div>
      <div class="card">
        <h4>PTO</h4>
        <div class="n">${esc(fmtHours(ptoMin))}</div>
      </div>
      <div class="card">
        <h4>Sick</h4>
        <div class="n">${esc(fmtHours(sickMin))}</div>
      </div>
      <div class="card accent">
        <h4>Total pay</h4>
        <div class="n">${esc(fmtMoney(totalPay))}</div>
      </div>
    </div>

    <div class="table-wrap">
      <div class="table-title">Shift Detail (${empEntries.length})</div>

      ${empEntries.length === 0 ? `
        <div class="empty">No shifts recorded for this period.</div>
      ` : `
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Type</th>
              <th>Job</th>
              <th>Start</th>
              <th>End</th>
              <th class="num">Total</th>
              <th class="num">Rate</th>
              <th class="num">Pay</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td class="label" colspan="6">Totals</td>
              <td class="num">${esc(fmtHours(totalMin))}</td>
              <td class="num"></td>
              <td class="num grand">${esc(fmtMoney(totalPay))}</td>
            </tr>
          </tfoot>
        </table>
      `}
    </div>

    <div class="sig">
      <div class="line">Employee signature</div>
      <div class="line">Manager signature</div>
    </div>

    <footer>
      <div>Confidential timesheet</div>
      <div class="code">Issued ${esc(generatedAt)}</div>
    </footer>
  </div>
</body>
</html>
  `;

  const w = window.open('', '_blank', 'width=1000,height=920');
  if (!w) {
    // Popup blocked — download as HTML file the user can open + print.
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-${(emp.name || 'employee').replace(/\s+/g, '-').toLowerCase()}-${startIso}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
};
